"use client";

import { z } from "zod";
import { useMemo, useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useForm, useWatch, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type {
  CheckoutSession,
  PaymentAttempt,
} from "@/lib/api/checkout.schema";
import {
  createSubmitPaymentSchema,
  type SubmitPaymentParams,
} from "@/lib/api/checkout.schema";
import { useSubmitPayment, useCancelPayment } from "@/hooks/use-checkout";
import { usePaymentPolling } from "@/hooks/use-payment-polling";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { parsePhoneNumber } from "react-phone-number-input";
import {
  INVALID_PHONE_ERROR,
  MOBILE_MONEY_PHONE_ERROR,
  phoneMarketIssue,
} from "@/lib/phone";
import {
  foreignCharge,
  marketCountries,
  type MarketPreview,
} from "@/lib/markets";
import { phoneCountry } from "@/lib/phone";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useCheckoutQuote } from "@/hooks/use-market-quote";
import { Text } from "@/components/ui/text";
import { Spinner } from "@/components/ui/spinner";
import { Field, FieldLabel } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";

import type { PaymentState } from "./payment.types";
import { formatAmount, formatFieldLabel } from "./payment.utils";
import { createSessionAmountFormatter } from "./session-amounts";
import {
  WaitingMobileView,
  WaitingQRView,
  WaitingCardView,
  PaymentFailedView,
  PaymentExpiredView,
} from "./views";
import { PaymentMethodSelector } from "./payment-method-selector";
import { SupportedProviders } from "./supported-providers";
import { SecuredBy } from "./secured-by";
import type { Dictionary } from "../../../dictionaries";

function normalizePhoneToE164(raw?: string | null): string {
  if (!raw) return "";
  const trimmed = raw.trim();
  const candidate =
    !trimmed.startsWith("+") && !trimmed.startsWith("0")
      ? `+${trimmed}`
      : trimmed;
  try {
    const parsed = parsePhoneNumber(candidate, "TZ");
    if (parsed?.number) return parsed.number;
  } catch {
    // fall through
  }
  return candidate.startsWith("+") ? candidate : "";
}

// Helper to convert active_attempt to PaymentState
function getInitialStateFromAttempt(
  attempt: PaymentAttempt | null | undefined,
): PaymentState {
  if (!attempt || attempt.status !== "pending") {
    return { status: "idle" };
  }

  switch (attempt.payment_method) {
    case "card":
      if (attempt.payment_url) {
        return {
          status: "waiting_card",
          result: {
            attempt_id: attempt.id,
            status: attempt.status,
            payment_url: attempt.payment_url,
          },
        };
      }
      break;
    case "qr":
      if (attempt.qr_code) {
        return {
          status: "waiting_qr",
          result: {
            attempt_id: attempt.id,
            status: attempt.status,
            qr_code: attempt.qr_code,
            payment_token: attempt.payment_token ?? undefined,
          },
        };
      }
      break;
    case "mobile_money":
      return {
        status: "waiting_mobile",
        result: {
          attempt_id: attempt.id,
          status: attempt.status,
          charged_amount: attempt.charged_amount,
          charged_currency: attempt.charged_currency,
        },
      };
  }

  return { status: "idle" };
}

interface PaymentFormProps {
  token: string;
  lang: string;
  session: CheckoutSession;
  dictionary: Dictionary;
  customAmount?: number;
  /** Indicative conversion for the market being previewed */
  preview?: MarketPreview | null;
  /** Reports the payer's phone country so the page can preview that market */
  onPayerCountryChange?: (country: string | null) => void;
}

export function PaymentForm({
  token,
  lang,
  session,
  dictionary,
  customAmount,
  preview = null,
  onPayerCountryChange,
}: PaymentFormProps) {
  const { checkout, placeholders } = dictionary;
  const router = useRouter();

  // Initialize payment state from active_attempt if session is active
  const [paymentState, setPaymentState] = useState<PaymentState>(() =>
    getInitialStateFromAttempt(session.active_attempt),
  );

  // Pick up an attempt that appears after a refetch
  const [seenAttempt, setSeenAttempt] = useState(session.active_attempt);
  if (session.active_attempt !== seenAttempt) {
    setSeenAttempt(session.active_attempt);
    if (session.active_attempt && paymentState.status === "idle") {
      const newState = getInitialStateFromAttempt(session.active_attempt);
      if (newState.status !== "idle") {
        setPaymentState(newState);
      }
    }
  }

  // Custom amount support
  const isCustomAmount = session.allow_custom_amount === true;
  const minAmount = session.min_amount ?? 500;
  const maxAmount = session.max_amount ?? 0;

  const requireName = !session.customer_name;
  const requireEmailFromSession =
    session.collect_email === true && !session.customer_email;

  // Prefilled flags — disable fields the API already provided
  const prefilledName = !!session.customer_name;
  const prefilledEmail = !!session.customer_email;
  const prefilledBilling = !!session.billing;

  // Markets the merchant collects from drive which numbers may pay
  const countries = useMemo(
    () => marketCountries(session.markets),
    [session.markets],
  );

  // Schema: email required when collect_email; card case handled via superRefine
  const schema = useMemo(() => {
    const base = createSubmitPaymentSchema({
      collectName: requireName,
      collectPhone: true,
      collectEmail: requireEmailFromSession,
      customAmount: isCustomAmount
        ? { min: minAmount, max: maxAmount }
        : undefined,
    });
    return base.superRefine((data, ctx) => {
      if (
        data.payment_method === "card" &&
        (!data.customer_email?.trim() ||
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.customer_email))
      ) {
        ctx.addIssue({
          path: ["customer_email"],
          message: "Email is required for card payments",
          code: z.ZodIssueCode.custom,
        });
      }
      if (data.payment_method !== "card" && data.customer_phone) {
        const issue = phoneMarketIssue(data.customer_phone, countries);
        if (issue) {
          ctx.addIssue({
            path: ["customer_phone"],
            message:
              issue === "unsupported"
                ? MOBILE_MONEY_PHONE_ERROR
                : INVALID_PHONE_ERROR,
            code: z.ZodIssueCode.custom,
          });
        }
      }
    });
  }, [
    requireName,
    requireEmailFromSession,
    isCustomAmount,
    minAmount,
    maxAmount,
    countries,
  ]);

  // Build default values for custom fields
  const customFieldDefaults = useMemo(() => {
    const defaults: Record<string, string> = {};
    session.custom_fields?.forEach((field) => {
      defaults[field.key] = "";
    });
    return defaults;
  }, [session.custom_fields]);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      payment_method: session.allowed_methods[0],
      amount: isCustomAmount && session.amount > 0 ? session.amount : undefined,
      customer_name: session.customer_name || "",
      customer_phone: normalizePhoneToE164(session.customer_phone),
      customer_email: session.customer_email || "",
      custom_data: customFieldDefaults,
      billing: session.billing || {
        first_name: "",
        last_name: "",
        address: "",
        city: "",
        state: "",
        postcode: "",
        country: "",
      },
    },
  });

  // Watch payment method for conditional billing and email fields
  const selectedPaymentMethod = useWatch({ control: form.control, name: "payment_method" });
  const showBilling = selectedPaymentMethod === "card";
  const showEmail =
    session.collect_email === true || selectedPaymentMethod === "card";

  // Sync custom amount from OrderSummary into the form
  useEffect(() => {
    if (isCustomAmount && customAmount !== undefined) {
      form.setValue("amount", customAmount, { shouldValidate: true });
    }
  }, [isCustomAmount, customAmount, form]);

  const customerPhone = useWatch({ control: form.control, name: "customer_phone" });

  // Let the page preview the payer's market as soon as their number is complete
  const payerCountry =
    customerPhone && isValidPhoneNumber(customerPhone)
      ? phoneCountry(customerPhone)
      : null;
  useEffect(() => {
    onPayerCountryChange?.(payerCountry);
  }, [payerCountry, onPayerCountryChange]);

  // The real quote: what this phone will be charged. Indicative until the
  // checkout response confirms it, but a refusal blocks paying.
  const phoneQuote = useCheckoutQuote(
    token,
    {
      phone: customerPhone,
      amount: isCustomAmount ? (customAmount ?? 0) : session.amount,
    },
    selectedPaymentMethod !== "card",
  );
  const quote = phoneQuote.data;
  const quoteRefused = !!quote && !quote.payable;
  const charge = foreignCharge(quote);

  // Derive billing country from the phone number's country code
  useEffect(() => {
    if (!showBilling || !customerPhone) return;
    try {
      const parsed = parsePhoneNumber(customerPhone);
      if (parsed?.country) {
        form.setValue("billing.country", parsed.country);
      }
    } catch {
      // ignore invalid phone while typing
    }
  }, [customerPhone, showBilling, form]);

  // Get button text from display settings or use default
  const buttonText = useMemo(() => {
    if (session.display?.button_text) return session.display.button_text;
    // A cross-border payer sees what their phone will actually be charged
    if (charge) {
      return `${checkout.pay} ${formatAmount(charge.value, charge.currency)}`;
    }
    const { formatPrice, formatFixedAmount } =
      createSessionAmountFormatter(session, preview);
    if (isCustomAmount) {
      return customAmount && customAmount > 0
        ? `${checkout.pay} ${formatPrice(customAmount)}`
        : checkout.pay;
    }
    return `${checkout.pay} ${formatFixedAmount()}`;
  }, [session, preview, charge, isCustomAmount, customAmount, checkout.pay]);

  const submitPayment = useSubmitPayment(token);
  const cancelPayment = useCancelPayment(token);

  const isWaiting =
    paymentState.status === "waiting_mobile" ||
    paymentState.status === "waiting_qr" ||
    paymentState.status === "waiting_card";

  usePaymentPolling({
    token,
    enabled: isWaiting,
    callbacks: {
      onCompleted: (data) => {
        if (data?.redirect_url) {
          window.location.assign(data.redirect_url);
          return;
        }
        router.push(`/${lang}/checkout/${token}/success`);
      },
      onFailed: () => {
        setPaymentState({ status: "failed" });
      },
      onExpired: () => {
        setPaymentState({ status: "expired" });
      },
    },
  });

  const onSubmit = form.handleSubmit(
    async (data) => {
      setPaymentState({ status: "submitting" });

      try {
        const result = await submitPayment.mutateAsync(
          data as SubmitPaymentParams,
        );

        // Use the payment method from form data to determine which view to show
        switch (data.payment_method) {
          case "card":
            // For card payments, redirect immediately to the payment URL
            if (result.payment_url) {
              window.location.assign(result.payment_url);
              return;
            }
            setPaymentState({ status: "waiting_card", result });
            break;
          case "qr":
            setPaymentState({ status: "waiting_qr", result });
            break;
          case "mobile_money":
          default:
            setPaymentState({ status: "waiting_mobile", result });
            break;
        }
      } catch {
        setPaymentState({ status: "idle" });
      }
    },
  );

  const handleCancel = async () => {
    try {
      const result =
        paymentState.status === "waiting_mobile" ||
        paymentState.status === "waiting_qr" ||
        paymentState.status === "waiting_card"
          ? paymentState.result
          : null;
      if (result?.attempt_id) {
        await cancelPayment.mutateAsync(result.attempt_id);
      }
    } catch {
      // Continue even if cancel fails
    }
    setPaymentState({ status: "idle" });
  };

  if (paymentState.status === "waiting_mobile") {
    const { result } = paymentState;
    return (
      <WaitingMobileView
        dictionary={dictionary}
        onCancel={handleCancel}
        amount={result.charged_amount ?? form.getValues("amount") ?? session.amount}
        currency={result.charged_currency ?? session.currency}
      />
    );
  }

  if (paymentState.status === "waiting_qr") {
    return (
      <WaitingQRView
        qrCode={paymentState.result.qr_code!}
        paymentToken={paymentState.result.payment_token ?? undefined}
        dictionary={dictionary}
        onCancel={handleCancel}
      />
    );
  }

  if (paymentState.status === "waiting_card") {
    // For card payments, redirect immediately to the payment URL
    if (paymentState.result.payment_url) {
      window.location.assign(paymentState.result.payment_url);
    }
    return (
      <WaitingCardView
        paymentUrl={paymentState.result.payment_url!}
        dictionary={dictionary}
        onCancel={handleCancel}
      />
    );
  }

  if (paymentState.status === "failed") {
    return (
      <PaymentFailedView
        reason={paymentState.reason}
        onRetry={handleCancel}
        dictionary={dictionary}
      />
    );
  }

  if (paymentState.status === "expired") {
    return (
      <PaymentExpiredView onRetry={handleCancel} dictionary={dictionary} />
    );
  }

  // Main payment form
  return (
    <div className="flex h-full flex-col md:rounded-xl">
      <form onSubmit={onSubmit} className="space-y-5">
        {/* Payment method selection */}
        <PaymentMethodSelector
          control={form.control}
          allowedMethods={session.allowed_methods}
          dictionary={dictionary}
        />

        {/* Phone number - always required */}
        <div>
          <Text variant="small" className="font-medium mb-2.5">
            {checkout.phoneNumber}
          </Text>
          <Controller
            name="customer_phone"
            control={form.control}
            render={({ field, fieldState }) => {
              const isMobileOnly = selectedPaymentMethod !== "card";
              // Only a number whose country is known can be unsupported;
              // a half-typed one is left alone until submit
              const valueIsSupported =
                !field.value ||
                phoneMarketIssue(field.value, countries) !== "unsupported";
              const unsupportedForMobile = isMobileOnly && !valueIsSupported;
              return (
                <div>
                  <PhoneInput
                    key={selectedPaymentMethod}
                    id={field.name}
                    value={field.value || undefined}
                    onChange={(val) => field.onChange(val ?? "")}
                    placeholder={placeholders.phone}
                    aria-invalid={
                      fieldState.invalid || unsupportedForMobile
                    }
                    countries={
                      isMobileOnly && valueIsSupported
                        ? [...countries]
                        : undefined
                    }
                  />
                  {unsupportedForMobile && (
                    <Text
                      variant="muted"
                      className="mt-1 text-xs text-destructive"
                    >
                      {MOBILE_MONEY_PHONE_ERROR}
                    </Text>
                  )}
                  {fieldState.error && !unsupportedForMobile && (
                    <Text
                      variant="muted"
                      className="mt-1 text-xs text-destructive"
                    >
                      {fieldState.error.message}
                    </Text>
                  )}
                  {!fieldState.error && !unsupportedForMobile && quoteRefused && (
                    <Text
                      variant="muted"
                      className="mt-1 text-xs text-destructive"
                    >
                      {quote.message || MOBILE_MONEY_PHONE_ERROR}
                    </Text>
                  )}
                  {!fieldState.error && !unsupportedForMobile && charge && (
                    <Text variant="muted" className="mt-1 text-xs">
                      {checkout.phoneCharged}{" "}
                      {formatAmount(charge.value, charge.currency)}
                    </Text>
                  )}
                </div>
              );
            }}
          />
        </div>

        {/* Customer details - Name (always required) & Email (optional based on collect_email) */}
        <div>
          <Text variant="small" className="font-medium mb-2.5">
            {checkout.contactInfo}
          </Text>
          <div className="overflow-hidden rounded-lg border border-border">
            <Controller
              name="customer_name"
              control={form.control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  id={field.name}
                  placeholder={checkout.fullName}
                  aria-invalid={fieldState.invalid}
                  disabled={prefilledName}
                  className={`rounded-none border-0 h-11 ${showEmail ? "border-b" : ""}`}
                />
              )}
            />

            {showEmail && (
              <Controller
                name="customer_email"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    id={field.name}
                    type="email"
                    placeholder={checkout.emailAddress}
                    aria-invalid={fieldState.invalid}
                    disabled={prefilledEmail}
                    className="rounded-none border-0 h-11"
                  />
                )}
              />
            )}
          </div>
          {(form.formState.errors.customer_name ||
            form.formState.errors.customer_email) && (
            <Text variant="muted" className="mt-1 text-xs text-destructive">
              {form.formState.errors.customer_name?.message ||
                form.formState.errors.customer_email?.message}
            </Text>
          )}
        </div>

        {/* Billing fields for card payments — country derived from phone number */}
        {showBilling && (
          <div>
            <div className="flex items-center justify-between mb-2.5">
              <Text variant="small" className="font-medium">
                {checkout.billingInfo}
              </Text>
              {prefilledBilling && (
                <Text variant="muted" className="text-xs text-emerald-600">
                  Prefilled
                </Text>
              )}
            </div>
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="grid grid-cols-2">
                <Controller
                  name="billing.first_name"
                  control={form.control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      value={field.value || ""}
                      placeholder={checkout.firstName}
                      aria-invalid={fieldState.invalid}
                      disabled={prefilledBilling}
                      autoComplete="billing given-name"
                      className="rounded-none border-0 border-b border-r h-11"
                    />
                  )}
                />
                <Controller
                  name="billing.last_name"
                  control={form.control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      value={field.value || ""}
                      placeholder={checkout.lastName}
                      aria-invalid={fieldState.invalid}
                      disabled={prefilledBilling}
                      autoComplete="billing family-name"
                      className="rounded-none border-0 border-b h-11"
                    />
                  )}
                />
              </div>
              <Controller
                name="billing.address"
                control={form.control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    value={field.value || ""}
                    placeholder={checkout.address}
                    aria-invalid={fieldState.invalid}
                    disabled={prefilledBilling}
                    autoComplete="billing street-address"
                    className="rounded-none border-0 border-b h-11"
                  />
                )}
              />
              <Controller
                name="billing.city"
                control={form.control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    value={field.value || ""}
                    placeholder={checkout.city}
                    aria-invalid={fieldState.invalid}
                    disabled={prefilledBilling}
                    autoComplete="billing address-level2"
                    className="rounded-none border-0 border-b h-11"
                  />
                )}
              />
              <div className="grid grid-cols-2">
                <Controller
                  name="billing.state"
                  control={form.control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      value={field.value || ""}
                      placeholder={checkout.state}
                      aria-invalid={fieldState.invalid}
                      disabled={prefilledBilling}
                      autoComplete="billing address-level1"
                      className="rounded-none border-0 border-r h-11"
                    />
                  )}
                />
                <Controller
                  name="billing.postcode"
                  control={form.control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      value={field.value || ""}
                      placeholder={checkout.postcode}
                      aria-invalid={fieldState.invalid}
                      disabled={prefilledBilling}
                      autoComplete="billing postal-code"
                      className="rounded-none border-0 h-11"
                    />
                  )}
                />
              </div>
            </div>
          </div>
        )}

        {/* Custom fields - at the end */}
        {session.custom_fields?.map((customField) => (
          <Controller
            key={customField.key}
            name={`custom_data.${customField.key}`}
            control={form.control}
            rules={{
              required: customField.required
                ? `${formatFieldLabel(customField.label)} is required`
                : false,
            }}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={field.name}>
                  {formatFieldLabel(customField.label)}
                  {!customField.required && (
                    <span className="text-muted-foreground/70 font-normal">
                      {" "}
                      ({checkout.optional})
                    </span>
                  )}
                </FieldLabel>
                {customField.type === "textarea" ? (
                  <Textarea
                    id={field.name}
                    name={field.name}
                    value={(field.value as string) || ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder={customField.placeholder}
                    aria-invalid={fieldState.invalid}
                    rows={3}
                  />
                ) : (
                  <Input
                    {...field}
                    value={(field.value as string) || ""}
                    id={field.name}
                    type={customField.type === "email" ? "email" : "text"}
                    placeholder={customField.placeholder}
                    aria-invalid={fieldState.invalid}
                    className="h-11"
                  />
                )}
                {fieldState.error && (
                  <Text variant="muted" className="text-xs text-destructive">
                    {fieldState.error.message}
                  </Text>
                )}
              </Field>
            )}
          />
        ))}

        {/* Submit button */}
        <div className="pt-2">
          <Button
            type="submit"
            size="lg"
            className="w-full h-12"
            disabled={
              paymentState.status === "submitting" ||
              submitPayment.isPending ||
              quoteRefused ||
              (isCustomAmount &&
                (!customAmount ||
                  customAmount < minAmount ||
                  (maxAmount > 0 && customAmount > maxAmount)))
            }
          >
            {paymentState.status === "submitting" || submitPayment.isPending ? (
              <Spinner className="size-4" />
            ) : (
              buttonText
            )}
          </Button>

          {submitPayment.isError && (
            <Text
              variant="muted"
              className="mt-2 text-center text-xs text-destructive"
            >
              {submitPayment.error.message}
            </Text>
          )}
        </div>
      </form>

      {/* Payment provider logos */}
      <div className="mt-6">
        <SupportedProviders dictionary={dictionary} />
      </div>

      {/* Secured by - mobile only */}
      <SecuredBy className="mt-6 mb-6 lg:hidden" dictionary={dictionary} />
    </div>
  );
}
